import { HS_HLOD_PAYLOAD_VERSION_JPEG, hsHlodPayloadVersion } from '../../../shared/hlod/payload-header.function';
import { hsReadStorageQuota } from '../../../shared/streaming/storage-quota.function';
import {
  hsIsConstrainedDevice,
  type HsDeviceEnvironment,
} from '../../../shared/streaming/tile-cache-eviction.function';
import {
  HsHlodIndexedDbPayloadStore,
  type HsHlodPayloadMeta,
  type HsHlodPayloadStore,
  type HsHlodPayloadStoreItem,
  type HsHlodStoredPayload,
} from './hlod-payload-store.class';

/** Downloads documents by id, as `HsPhotogrammetrySourceOptions.downloadDocuments` does. */
export type HsHlodDocumentDownload = (documentIds: string[]) => Promise<Map<string, ArrayBuffer>>;

export interface HsHlodPayloadCacheOptions {
  /** Where documents are kept; default an `HsHlodIndexedDbPayloadStore` on `databaseName`. */
  store?: HsHlodPayloadStore;
  /** Database of the default store (`hyperscene-hlod`). */
  databaseName?: string;
  indexedDb?: IDBFactory;
  /** Budget for the stored payloads; default `hsDefaultHlodPayloadCacheBytes()`, at most half of the storage quota. */
  maxBytes?: number;
  /**
   * Version-1 (JPEG) node payloads are downloaded again in the background once this much time passed since their
   * last download, so a cached node picks up the KTX2 payload the deferred pass wrote under the same id. Default one
   * hour; at most once per document and session.
   */
  revalidateAfterMs?: number;
  /** Wall clock in milliseconds (`Date.now`); eviction order and revalidation ages persist across sessions. */
  now?: () => number;
  onWarning?(message: string, error: unknown): void;
}

/** Counters of one cache since it was created; `storedBytes` and `entries` are as of the last store scan. */
export interface HsHlodPayloadCacheStats {
  hits: number;
  misses: number;
  /** Bytes served from the cache instead of the network. */
  hitBytes: number;
  /** Bytes downloaded for misses. */
  missBytes: number;
  writes: number;
  writtenBytes: number;
  evictions: number;
  evictedBytes: number;
  /** Version-1 payloads downloaded again in the background, and how many of those had become version 2. */
  revalidations: number;
  replaced: number;
  /** Store failures; after repeated write failures the cache stops writing for the session. */
  errors: number;
  storedBytes: number;
  entries: number;
}

export const HS_DESKTOP_HLOD_PAYLOAD_CACHE_BYTES: number = 3 * 1024 * 1024 * 1024;
export const HS_MOBILE_HLOD_PAYLOAD_CACHE_BYTES: number = 1024 * 1024 * 1024;
/** Below this the cache stops shrinking its budget after quota errors and turns itself off instead. */
const MIN_BUDGET_BYTES: number = 16 * 1024 * 1024;
const MAX_WRITE_FAILURES: number = 3;
const DEFAULT_REVALIDATE_AFTER_MS: number = 60 * 60 * 1000;

/** Default budget for the persistent payload cache: 3 GiB, 1 GiB on phones, tablets and low-memory devices. */
export function hsDefaultHlodPayloadCacheBytes(environment?: HsDeviceEnvironment): number {
  return hsIsConstrainedDevice(environment) ? HS_MOBILE_HLOD_PAYLOAD_CACHE_BYTES : HS_DESKTOP_HLOD_PAYLOAD_CACHE_BYTES;
}

/**
 * Keeps HLOD tileset documents (manifests, subtrees, node payloads) across sessions, keyed by document id. Hits
 * skip the network; misses are downloaded through the caller's function and written in the background. The least
 * recently used documents go first when the budget is exceeded. A cached version-1 payload (JPEG atlas) is served
 * at once and downloaded again in the background after `revalidateAfterMs`, because the deferred KTX2 pass replaces
 * it under the same document id; version-2 payloads and manifests never change, so they are never checked. Store
 * failures turn the cache off for the session; the download path keeps working.
 */
export class HsHlodPayloadCache {
  /** Reads and writes pass through the store; false makes every fetch a plain download. */
  public enabled: boolean = true;
  public maxBytes: number;
  public readonly stats: HsHlodPayloadCacheStats = {
    hits: 0,
    misses: 0,
    hitBytes: 0,
    missBytes: 0,
    writes: 0,
    writtenBytes: 0,
    evictions: 0,
    evictedBytes: 0,
    revalidations: 0,
    replaced: 0,
    errors: 0,
    storedBytes: 0,
    entries: 0,
  };
  private readonly store: HsHlodPayloadStore;
  private readonly revalidateAfterMs: number;
  private readonly now: () => number;
  private usable: boolean = true;
  private writeFailures: number = 0;
  /** Bytes in the store as of the last scan, or null before the first one. */
  private storedBytes: number | null = null;
  /** Documents already revalidated this session, whether or not the download succeeded. */
  private readonly revalidated: Set<string> = new Set();
  /** Store writes and prunes run one after another, in order. */
  private writes: Promise<void> = Promise.resolve();
  /** Background downloads run apart from the writes, so a slow network never holds them. */
  private revalidations: Promise<void> = Promise.resolve();
  private quotaChecked: boolean = false;

  public constructor(private readonly options: HsHlodPayloadCacheOptions = {}) {
    this.store =
      options.store ??
      new HsHlodIndexedDbPayloadStore({ databaseName: options.databaseName, indexedDb: options.indexedDb });
    this.maxBytes = options.maxBytes ?? hsDefaultHlodPayloadCacheBytes();
    this.revalidateAfterMs = options.revalidateAfterMs ?? DEFAULT_REVALIDATE_AFTER_MS;
    this.now = options.now ?? ((): number => Date.now());
    this.quotaChecked = options.maxBytes !== undefined;
  }

  /** False once the store failed; `enabled` may still be true. */
  public get available(): boolean {
    return this.usable;
  }

  /** Cached documents among `documentIds`, the rest downloaded with `download`; the result holds every id returned. */
  public async fetch(documentIds: string[], download: HsHlodDocumentDownload): Promise<Map<string, ArrayBuffer>> {
    if (!this.enabled || !this.usable || documentIds.length === 0) {
      return download(documentIds);
    }
    this.initialize();
    let cached: Map<string, HsHlodStoredPayload>;
    try {
      cached = await this.store.read(documentIds);
    } catch (error: unknown) {
      this.stats.errors++;
      this.disable('HLOD payload cache cannot be read; it is off for this session.', error);
      return download(documentIds);
    }
    const result: Map<string, ArrayBuffer> = new Map();
    const missing: string[] = [];
    const hits: string[] = [];
    const stale: string[] = [];
    const now: number = this.now();
    documentIds.forEach((id: string) => {
      const entry: HsHlodStoredPayload | undefined = cached.get(id);
      if (entry === undefined) {
        missing.push(id);
        return;
      }
      result.set(id, entry.payload);
      hits.push(id);
      this.stats.hits++;
      this.stats.hitBytes += entry.payload.byteLength;
      if (
        entry.meta.version === HS_HLOD_PAYLOAD_VERSION_JPEG &&
        !this.revalidated.has(id) &&
        now - entry.meta.validatedAt >= this.revalidateAfterMs
      ) {
        stale.push(id);
      }
    });
    if (missing.length > 0) {
      this.stats.misses += missing.length;
      const downloaded: Map<string, ArrayBuffer> = await download(missing);
      const items: HsHlodPayloadStoreItem[] = [];
      missing.forEach((id: string) => {
        const payload: ArrayBuffer | undefined = downloaded.get(id);
        if (payload === undefined) {
          return;
        }
        result.set(id, payload);
        this.stats.missBytes += payload.byteLength;
        items.push({ id, payload, meta: this.makeMeta(payload, now) });
      });
      this.enqueueWrite(items);
    }
    if (hits.length > 0) {
      this.enqueue(() => this.store.touch(hits, now));
    }
    if (stale.length > 0) {
      stale.forEach((id: string) => this.revalidated.add(id));
      this.revalidations = this.revalidations.then(() => this.revalidate(stale, download));
    }
    return result;
  }

  /** Removes every stored document. */
  public async clear(): Promise<void> {
    await this.flush();
    await this.store.clear();
    this.storedBytes = 0;
    this.stats.storedBytes = 0;
    this.stats.entries = 0;
  }

  /** Resolves once the pending writes, evictions and background revalidations are done. */
  public async flush(): Promise<void> {
    await this.revalidations;
    await this.writes;
  }

  /** Entries and bytes in the store, scanned now; also refreshes `stats.storedBytes` and `stats.entries`. */
  public async usage(): Promise<{ entries: number; bytes: number }> {
    await this.flush();
    const entries: { meta: HsHlodPayloadMeta }[] = await this.store.entries();
    const bytes: number = entries.reduce(
      (sum: number, entry: { meta: HsHlodPayloadMeta }) => sum + entry.meta.bytes,
      0
    );
    this.storedBytes = bytes;
    this.stats.storedBytes = bytes;
    this.stats.entries = entries.length;
    return { entries: entries.length, bytes };
  }

  /** Before the first fetch: size the budget by the storage quota and learn what the store holds. */
  private initialize(): void {
    if (this.storedBytes === null) {
      this.storedBytes = 0;
      this.enqueue(() => this.prune());
    }
    if (!this.quotaChecked) {
      this.quotaChecked = true;
      void hsReadStorageQuota().then((quota: number | null) => {
        if (quota !== null) {
          this.maxBytes = Math.min(this.maxBytes, Math.floor(quota / 2));
        }
      });
    }
  }

  private async revalidate(documentIds: string[], download: HsHlodDocumentDownload): Promise<void> {
    if (!this.enabled || !this.usable) {
      return;
    }
    let fresh: Map<string, ArrayBuffer>;
    try {
      fresh = await download(documentIds);
    } catch (error: unknown) {
      this.stats.errors++;
      this.options.onWarning?.('HLOD payload revalidation failed.', error);
      return;
    }
    const now: number = this.now();
    const items: HsHlodPayloadStoreItem[] = [];
    documentIds.forEach((id: string) => {
      const payload: ArrayBuffer | undefined = fresh.get(id);
      if (payload === undefined) {
        return;
      }
      const meta: HsHlodPayloadMeta = this.makeMeta(payload, now);
      this.stats.revalidations++;
      if (meta.version !== HS_HLOD_PAYLOAD_VERSION_JPEG) {
        this.stats.replaced++;
      }
      items.push({ id, payload, meta });
    });
    this.enqueueWrite(items);
  }

  private makeMeta(payload: ArrayBuffer, now: number): HsHlodPayloadMeta {
    return { bytes: payload.byteLength, touchedAt: now, version: hsHlodPayloadVersion(payload), validatedAt: now };
  }

  private enqueueWrite(items: HsHlodPayloadStoreItem[]): void {
    if (items.length === 0) {
      return;
    }
    this.enqueue(async () => {
      try {
        await this.store.write(items);
      } catch (error: unknown) {
        this.onWriteFailure(error);
        return;
      }
      this.writeFailures = 0;
      const bytes: number = items.reduce((sum: number, item: HsHlodPayloadStoreItem) => sum + item.meta.bytes, 0);
      this.stats.writes += items.length;
      this.stats.writtenBytes += bytes;
      this.storedBytes = (this.storedBytes ?? 0) + bytes;
      this.stats.storedBytes = this.storedBytes;
      this.stats.entries += items.length;
      if (this.storedBytes > this.maxBytes) {
        await this.prune();
      }
    });
  }

  /** Scans the store and deletes the least recently used documents until the budget holds. */
  private async prune(): Promise<void> {
    const entries: { id: string; meta: HsHlodPayloadMeta }[] = await this.store.entries();
    let total: number = entries.reduce((sum: number, entry: { meta: HsHlodPayloadMeta }) => sum + entry.meta.bytes, 0);
    const doomed: string[] = [];
    let doomedBytes: number = 0;
    for (const entry of entries) {
      if (total <= this.maxBytes) {
        break;
      }
      doomed.push(entry.id);
      doomedBytes += entry.meta.bytes;
      total -= entry.meta.bytes;
    }
    if (doomed.length > 0) {
      await this.store.delete(doomed);
      this.stats.evictions += doomed.length;
      this.stats.evictedBytes += doomedBytes;
    }
    this.storedBytes = total;
    this.stats.storedBytes = total;
    this.stats.entries = entries.length - doomed.length;
  }

  private onWriteFailure(error: unknown): void {
    this.stats.errors++;
    this.writeFailures++;
    const quota: boolean = error instanceof Error && error.name === 'QuotaExceededError';
    if (quota && this.maxBytes / 2 >= MIN_BUDGET_BYTES) {
      // The browser has less room than the budget: halve it and make room before the next write.
      this.maxBytes = Math.floor(this.maxBytes / 2);
      this.options.onWarning?.(`HLOD payload cache budget reduced to ${this.maxBytes} bytes.`, error);
      this.storedBytes = null;
      this.enqueue(() => this.prune());
      return;
    }
    if (this.writeFailures >= MAX_WRITE_FAILURES || quota) {
      this.disable('HLOD payload cache cannot be written; it is off for this session.', error);
    }
  }

  private disable(message: string, error: unknown): void {
    this.usable = false;
    this.options.onWarning?.(message, error);
  }

  private enqueue(work: () => Promise<void>): void {
    this.writes = this.writes.then(work).catch((error: unknown) => {
      this.stats.errors++;
      this.options.onWarning?.('HLOD payload cache maintenance failed.', error);
    });
  }
}
